import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'kromix_webshop_cart';

// Guideline / standard indicative prices for Kromix assortment if not in CMS
export const DEFAULT_PRICES = {
  'kromix-fiberkost': 389,
  'kromix-naturkost': 369,
  'kromix-favorit': 429,
  'kromix-lam-ris': 419,
  'kromix-minies': 229,
  'kromix-junior': 399,
  'kromix-minikost': 389,
  'kromix-katteknas': 349,
  'kromix-lakseolie': 189,
  'kromix-hvalpemaelk': 249,
  'kromix-knuspies': 149,
  'kromix-loopies': 49,
  'kromix-avant-cat-classic': 169,
  'kromix-avant-cat-exellent': 189,
  'kromix-solsikke': 179,
  'kromix-vildtfugleblanding': 219,
};

export function getProductPrice(product, variant = '') {
  if (!product) return 0;
  // If CMS contains explicit price
  if (product.pris !== undefined && product.pris !== null && product.pris !== '') {
    const parsed = typeof product.pris === 'number' ? product.pris : parseFloat(String(product.pris).replace(/[^0-9.]/g, ''));
    if (!isNaN(parsed) && parsed > 0) return parsed;
  }
  const slug = typeof product.slug === 'object'
    ? (product.slug?.da || product.slug?._ || Object.values(product.slug)[0] || '')
    : product.slug || '';
  if (DEFAULT_PRICES[slug]) {
    const base = DEFAULT_PRICES[slug];
    const v = (variant || '').toLowerCase().trim();
    if (v === '5 kg' || v === '4 kg' || v === '3 kg' || v.startsWith('5 kg ') || v.startsWith('4 kg ') || v.startsWith('3 kg ')) {
      return Math.round(base * 0.45);
    }
    return base;
  }
  return 0;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.warn('Could not read cart from localStorage', e);
      return [];
    }
  });

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  // Sync to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn('Could not save cart to localStorage', e);
    }
  }, [items]);

  // Toast auto-hide
  useEffect(() => {
    if (!toastMessage) return;
    const timer = setTimeout(() => {
      setToastMessage(null);
    }, 3500);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  const addToCart = (product, quantity = 1, selectedVariant = null, explicitPrice = null) => {
    if (!product) return;

    const slug = typeof product.slug === 'object'
      ? (product.slug?.da || product.slug?._ || Object.values(product.slug)[0] || '')
      : product.slug || '';
    const variant = selectedVariant || product.variant_stoerrelse || 'Standard';
    const lineId = `${slug || product.navn}-${variant}`.replace(/\s+/g, '-').toLowerCase();
    const image = product.primaert_billede?.[0] || product.galleri?.[0] || '';
    const unitPrice = explicitPrice !== null ? explicitPrice : getProductPrice(product, variant);

    setItems((prevItems) => {
      const existingIndex = prevItems.findIndex((it) => it.id === lineId);
      if (existingIndex > -1) {
        const copy = [...prevItems];
        copy[existingIndex] = {
          ...copy[existingIndex],
          quantity: copy[existingIndex].quantity + quantity,
        };
        return copy;
      }
      return [
        ...prevItems,
        {
          id: lineId,
          slug,
          name: product.navn,
          variant,
          image,
          price: unitPrice,
          category: product.kategori || product.dyretype || '',
          quantity,
        },
      ];
    });

    setToastMessage({
      title: 'Tilføjet til bestilling',
      name: product.navn,
      variant,
      quantity,
    });
  };

  const updateQuantity = (itemId, newQty) => {
    if (newQty <= 0) {
      removeFromCart(itemId);
      return;
    }
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, quantity: newQty } : item))
    );
  };

  const removeFromCart = (itemId) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const clearCart = () => {
    setItems([]);
  };

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  const totalItems = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const totalPrice = items.reduce(
    (sum, item) => sum + (item.price || 0) * (item.quantity || 0),
    0
  );
  const hasPrices = items.some((item) => (item.price || 0) > 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        isCartOpen,
        setIsCartOpen,
        openCart,
        closeCart,
        totalItems,
        totalPrice,
        hasPrices,
        toastMessage,
        setToastMessage,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart skal bruges inden i en CartProvider');
  }
  return ctx;
}
